import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, UserPlus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const ROLE_COLOR = { owner: "bg-blue-100 text-blue-700", manager: "bg-emerald-100 text-emerald-700", viewer: "bg-slate-100 text-slate-600" };

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [invite, setInvite] = useState(null);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", org_role: "viewer" });

  const load = () => api.get("/org").then(({ data }) => setData(data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!f.first_name || !f.email) return toast.error("Name and email required");
    try { const { data } = await api.post("/org/members", f); setInvite(data); setF({ first_name: "", last_name: "", email: "", org_role: "viewer" }); load(); toast.success("Member invited"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const changeRole = async (mid, role) => { try { await api.put(`/org/members/${mid}/role`, { org_role: role }); toast.success("Role updated"); load(); } catch (e) { toast.error("Failed"); } };
  const remove = async (mid) => { try { await api.delete(`/org/members/${mid}`); toast.success("Member removed"); load(); } catch (e) { toast.error("Failed"); } };

  if (data === false) return <DashboardLayout nav={getNav("client")} title="Team"><p>Unable to load.</p></DashboardLayout>;

  return (
    <DashboardLayout nav={getNav("client")} title="Team & Organization">
      {!data ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div><h2 className="font-head text-xl font-semibold text-slate-900">{data.organization}</h2><p className="text-sm text-slate-500">{data.members.length} team member{data.members.length === 1 ? "" : "s"} · your role: <span className="font-medium capitalize">{data.my_role}</span></p></div>
            {data.can_manage && (
              <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setInvite(null); }}>
                <DialogTrigger asChild><Button data-testid="invite-member-btn" className="bg-blue-600 hover:bg-blue-700"><UserPlus className="w-4 h-4 mr-1" /> Invite Member</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-head">Invite Team Member</DialogTitle></DialogHeader>
                  {invite ? (
                    <div className="space-y-3">
                      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900">
                        <p className="font-semibold">Member created!</p>
                        <p className="mt-2">Email: <span className="font-mono">{invite.email}</span></p>
                        <p>Temp password: <span className="font-mono" data-testid="invite-temp-pw">{invite.temp_password}</span></p>
                        <p className="mt-2 text-xs">Share these credentials securely. They can change the password after signing in.</p>
                      </div>
                      <Button onClick={() => { setInvite(null); setOpen(false); }} className="w-full bg-blue-600 hover:bg-blue-700">Done</Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>First Name</Label><Input data-testid="invite-first" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className="mt-1.5" /></div>
                        <div><Label>Last Name</Label><Input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className="mt-1.5" /></div>
                      </div>
                      <div><Label>Email</Label><Input data-testid="invite-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" /></div>
                      <div><Label>Role</Label>
                        <Select value={f.org_role} onValueChange={(v) => setF({ ...f, org_role: v })}><SelectTrigger data-testid="invite-role" className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="manager">Manager (can manage properties & requests)</SelectItem><SelectItem value="viewer">Viewer (read-only)</SelectItem></SelectContent></Select>
                      </div>
                      <Button data-testid="invite-send" onClick={send} className="w-full bg-blue-600 hover:bg-blue-700">Create Member</Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
            {data.members.map((m) => (
              <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">{(m.first_name?.[0] || "") + (m.last_name?.[0] || "")}</div>
                  <div><div className="font-medium text-slate-900">{m.first_name} {m.last_name}</div><div className="text-xs text-slate-500">{m.email}</div></div>
                </div>
                <div className="flex items-center gap-2">
                  {m.org_role === "owner" || !data.can_manage ? (
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${ROLE_COLOR[m.org_role]}`}>{m.org_role}</span>
                  ) : (
                    <>
                      <Select value={m.org_role} onValueChange={(v) => changeRole(m.id, v)}><SelectTrigger data-testid={`member-role-${m.id}`} className="h-8 w-32"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="manager">Manager</SelectItem><SelectItem value="viewer">Viewer</SelectItem></SelectContent></Select>
                      <Button data-testid={`member-remove-${m.id}`} size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={() => remove(m.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
